<template>
  <div>
    <counterparty-edit
      v-if="editModel.counterpartyId != null"
      :model="editModel"
      :marketId="marketId"
      :clientId="clientId"
      @cancel="cancelEdit"
      @save="refresh"
    >
    </counterparty-edit>
    <page-toolbar>
      <kendo-button icon="add" title="Add new" v-if="!isReadOnly" @click="createButtonClick">
      </kendo-button>
      <section v-show="!isMaster">
        <kendo-dropdownlist
          :data-source="marketDataSource"
          :data-text-field="'market'"
          :data-value-field="'marketId'"
          v-model="marketId"
          @databound="marketDataBound"
          @change="marketChange"
          style="width: 100px;"
        >
        </kendo-dropdownlist>
      </section>
      <div class="exportButton"></div>
    </page-toolbar>

    <kendo-grid
      height="calc(100vh - 160px)"
      :dataSource="dataSource"
      :editable="'inline'"
      :autoBind="false"
      @edit="edit"
      ref="grid"
      :filterable="true"
      :pageable-always-visible="true"
      :data-source="this.dataSource"
      :columns="columns"
    >
    </kendo-grid>
  </div>
</template>

<script>
import CounterpartyEdit from '@/components/Config/Counterparty/EditForm'
import {enableExportExcel} from '@/helpers/export-helper'
import {checkboxColumn} from '@/helpers/kendo-editors'
import PageToolbar from '@/components/PageToolbar'
import {mapGetters} from 'vuex'
import security from '@/mixins/security'

export default {
  mixins: [security],
  mounted() {
    const grid = this.$refs.grid.kendoWidget()
    let pageToolbar = kendo.jQuery('.page_toolbar-item')
    let excelContainer = pageToolbar.find('.exportButton')
    enableExportExcel(grid, '/api/config/counterparty/exportToExcel', excelContainer)
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    isMaster() {
      return this.clientId == 0
    },
    columns() {
      return [
        {
          editable: !this.isReadOnly,
          command: [{name: 'edit'}],
          width: 60,
        },
        {title: 'Code', field: 'code', width: 150},
        {title: 'Description', field: 'description', width: 250},
        {title: 'Curve', field: 'curveName', width: 150},
        checkboxColumn(
          'clientBank',
          {
            title: 'Client Bank',
            width: 150,
            hidden: this.isMaster,
          },
          false,
        ),
        checkboxColumn(
          'isDefault',
          {
            title: 'Default',
            width: 150,
            hidden: this.isMaster,
          },
          false,
        ),
        {
          title: 'Future Contract Commission',
          field: 'futureContractCommission',
          width: 132,
          format: '{0:n2}',
          hidden: this.isMaster,
          attributes: {style: 'text-align: right;'},
        },
        {title: 'Address', field: 'address', width: 400},
        {title: 'Phone Number', field: 'phoneNumber', width: 200},
        {title: 'Email', field: 'email', width: 250},
        {
          title: 'Limit',
          field: 'limit',
          width: 100,
          format: '{0:n0}',
          attributes: {style: 'text-align: right;'},
        },
        {
          editable: !this.isReadOnly,
          command: [{name: 'destroy', visible: dataItem => dataItem.canBeDeleted}],
          width: 60,
        },
        {},
      ].filter(c => c.editable !== false)
    },
  },
  components: {
    CounterpartyEdit,
    PageToolbar,
  },
  methods: {
    createButtonClick() {
      const grid = this.$refs.grid.kendoWidget()
      grid.addRow()
    },
    marketDataBound(e) {
      e.sender.select(0)
      this.marketId = e.sender.value()
      e.sender.trigger('change')
    },
    marketChange(e) {
      this.marketId = e.sender.value()
      this.dataSource.read()
    },
    edit(e) {
      this.editModel = {uid: e.model.uid, ...e.model.toJSON()}
      e.preventDefault()
      const grid = this.$refs.grid.kendoWidget()
      grid.cancelRow()
    },
    cancelEdit() {
      this.editModel = {}
      const grid = this.$refs.grid.kendoWidget()
      grid.cancelChanges()
    },
    refresh() {
      this.$refs.grid.kendoWidget().dataSource.read()
      this.editModel = {}
    },
  },
  data() {
    return {
      marketId: null,
      editModel: {},
      marketDataSource: new kendo.data.DataSource({
        transport: {
          read: {
            url: '/api/config/counterparty/getUserMarkets',
            contentType: 'application/json',
            type: 'GET',
            data: () => ({
              clientId: this.clientId,
            }),
          },
        },
      }),
      dataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        pageSize: 15,
        transport: {
          create: {
            type: 'POST',
            url: '/api/config/counterparty',
            data: () => ({clientId: this.clientId}),
            dataType: 'json',
          },
          read: {
            type: 'GET',
            url: '/api/config/counterparty',
            data: () => ({
              clientId: this.clientId,
              marketId: this.marketId,
            }),
            dataType: 'json',
          },
          update: {
            type: 'PUT',
            url: '/api/config/counterparty',
            dataType: 'json',
            data: () => ({clientId: this.clientId, marketId: this.marketId}),
            complete: () => {
              this.$refs.grid.kendoWidget().dataSource.read()
            },
          },
          destroy: {
            type: 'DELETE',
            url: '/api/config/counterparty',
            data: () => ({clientId: this.clientId, marketId: this.marketId}),
            dataType: 'json',
          },
        },
        schema: {
          data: 'data',
          errors: 'errors',
          total: 'total',
          model: {
            id: 'counterpartyId',
            fields: {
              counterpartyId: {
                field: 'counterpartyId',
                type: 'number',
                validation: {required: true},
              },
              code: {field: 'code', type: 'string', validation: {required: true}},
              description: {field: 'description', type: 'string', validation: {required: true}},
              curveId: {type: 'number', defaultValue: -1},
              curveName: {type: 'string'},
              address: {type: 'string'},
              phoneNumber: {type: 'string'},
              limit: {type: 'number'},
              email: {type: 'string'},
              canEdit: {type: 'bool', defaultValue: true, editable: false},
              clientBank: {type: 'bool', defaultValue: false, editable: true},
              isDefault: {type: 'bool', defaultValue: false, editable: true},
              futureContractCommission: {type: 'number'},
              canBeDeleted: {type: 'bool', defaultValue: true, editable: false},
            },
          },
        },
      }),
    }
  },
}
</script>
